import { AnyBoosterType, BoosterType, PreBoosterType } from '../State/Card/BoosterType';
import { report } from '../report';
import { IPlayerState } from '../State/Player/IPlayerState';
import { Immutable } from './Types';
import { FTUXType } from '../Player/PlayerProperty/Progress/FTUXType';
import { ProductId } from 'Utility/Product';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { getBoosterData } from 'State/Card/BoosterDataMap';
import { RewardType } from './Rewards/RewardType';

/**
 * @deprecated We don't need to append to this anymore
 */
// export const upcomingProductIds: string[] = [];

export interface IWeightedRewardData {
    odds: number;
    type: RewardType;
    value: number;
}

export interface IAPReward {
    id: ProductId;
    index: number;
    usdPrice: number;
    usdPriceWithoutDiscount?: number;
    usdMultiplier?: number;
    usdPriceScaling?: number;
    percent?: number;
    rewards: IRewardData[];
}

export interface IRewardData {
    type: RewardType;
    value: number;
    fallback?: IRewardData;
    [key: string]: any;
}

export enum RewardPools {
    DAILY_LOGIN = 'dailyLoginRewards',
    CURRENCY_SHARE = 'currencyShareRewards',
    FRIEND_GIFT = 'friendGiftRewards',
}

interface IRewardSettings {
    localizationKey: string;
    texture: string;
    ftuxRequirement?: FTUXType;
}

const rewardDataMap: Record<RewardType, IRewardSettings> = {
    [RewardType.COINS]: {
        localizationKey: 'RewardCoins',
        texture: 'coin-reward',
    },
    [RewardType.BOMB]: getBoosterData(BoosterType.TNT),
    [RewardType.JOKER]: getBoosterData(BoosterType.JOKER),
    [RewardType.UNDO]: getBoosterData(BoosterType.UNDO),
    [RewardType.BULLSEYE]: getBoosterData(BoosterType.BULLS_EYE),
    [RewardType.SHUFFLE]: getBoosterData(BoosterType.SHUFFLE),
    [RewardType.NONE]: null,
    [RewardType.THREE_STAR_REWARD]: null,
    [RewardType.PRIZE_WHEEL_SPIN]: null,
    [RewardType.CARDS]: null,
    [RewardType.OTHER]: null,
    [RewardType.PLUS_FIVE_CARDS]: getBoosterData(BoosterType.PLUS_FIVE),
    [RewardType.FREE_PLAY]: {
        localizationKey: 'FreeLevel',
        texture: 'icon_free_level',
    },
    [RewardType.PET]: null,
    [RewardType.PRE_BOOSTER_REMOVE_THREE]: getBoosterData(PreBoosterType.REMOVE_THREE),
    [RewardType.PRE_BOOSTER_JOKER]: getBoosterData(PreBoosterType.JOKER),
    [RewardType.PRE_BOOSTER_CANISTER]: getBoosterData(PreBoosterType.CANISTER),
    [RewardType.PREMIUM_SPIN]: null,
    [RewardType.FARM_AREA]: null,
    // TODO: Add data for localization and texture for deco item rewards
    [RewardType.FARM_ITEM]: null,
    [RewardType.PLINKO_TOKEN]: null,
} as const;

export function getRewardData(type: RewardType): IRewardSettings {
    const data = rewardDataMap[type];
    if (!data) {
        report('Unable to find data for given reward type', {
            once: true,
            tags: { type },
        });
        return {
            localizationKey: 'localizationKey',
            texture: 'blank',
        };
    }
    return data;
}

export function isRewardUnlocked(state: Immutable<IPlayerState>, rewardType: RewardType): boolean {
    const requirement = getRewardData(rewardType).ftuxRequirement;
    // Return true if there is no ftux requirement for the given reward type
    return requirement ? FTUXStateHelper.isFtuxCompleted(state, requirement) : true;
}

export const rewardTypeByBoosterType: Record<AnyBoosterType, RewardType | null> = {
    [BoosterType.JOKER]: RewardType.JOKER,
    [BoosterType.UNDO]: RewardType.UNDO,
    [BoosterType.TNT]: RewardType.BOMB,
    [BoosterType.SHUFFLE]: RewardType.SHUFFLE,
    [BoosterType.BULLS_EYE]: RewardType.BULLSEYE,
    [BoosterType.PLUS_FIVE]: RewardType.PLUS_FIVE_CARDS,
    [BoosterType.PLUS_THREE]: null,
    [BoosterType.PLUS_ONE]: RewardType.CARDS,

    [PreBoosterType.REMOVE_THREE]: RewardType.PRE_BOOSTER_REMOVE_THREE,
    [PreBoosterType.JOKER]: RewardType.PRE_BOOSTER_JOKER,
    [PreBoosterType.CANISTER]: RewardType.PRE_BOOSTER_CANISTER,
};

export const boosterTypeByRewardType: Record<RewardType, AnyBoosterType | null> = Object.entries(
    rewardTypeByBoosterType
).reduce((acc, [key, value]: [AnyBoosterType, RewardType | null]) => {
    if (value) {
        acc[value] = key;
    }

    return acc;
}, {} as Record<RewardType, AnyBoosterType | null>);
